import FileAppPicture from '@/assets/images/file-app.png?w=24&format=avif;webp&as=picture';
import ZoomFit from '@/assets/images/icons/zoom-fit.svg?react';
import type { ImageZoomActions } from '@/components/common/media-content/image-zoom-pan';
import MediaContent from '@/components/common/media-content/media-content';
import PicturizedImage from '@/components/common/picturized-image';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import DelayedLayout from '@/components/layouts/progress-layout/delayed-layout';
import useDeleteMessages from '@/hooks/conversations/use-delete-messages';
import useMediaMessages from '@/hooks/conversations/use-media-messages';
import useDownloadUrl from '@/hooks/use-download-url';
import useUserPlatform from '@/hooks/use-user-platform';
import { ConversationMessageContentType } from '@/models/conversations/conversation';
import type { ConversationType } from '@/models/conversations/conversation-session';
import ROUTES from '@/routes';
import { makePrompt } from '@/stores/prompter';
import { getMessageDownloadParams } from '@/utils/get-message-downloadable';
import cn from '@/utils/merge-class-names';
import { parseCollectionPreviewDate } from '@/utils/parseTime';
import 'react-splide-ts/css';
import { Add, Category, Export, ImportCurve, SearchZoomIn, SearchZoomOut1, Trash } from 'iconsax-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { useTypedParams, useTypedState } from 'react-router-typesafe-routes';
import type { SplideEventHandlers } from 'react-splide-ts';
import { Splide, SplideSlide } from 'react-splide-ts';

interface Props {
  type: ConversationType;
}

export default function MediaMessageByIdRoute({ type }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id, messageId: mediaId } = useTypedParams(
    ROUTES.MESSAGES.GROUPS.MEDIA.BY_ID || ROUTES.MESSAGES.SESSIONS.MEDIA.BY_ID,
  );
  const [messageId, setMessageId] = useState(() => mediaId);
  const { messages, messageIndex, message } = useMediaMessages(messageId);
  const shouldDarkMode = message
    ? [ConversationMessageContentType.Image, ConversationMessageContentType.Video].includes(message.MsgType)
    : false;

  const { handleDownloadUrl, isLoading: isDownloading } = useDownloadUrl();
  const { promptDeleteMessages } = useDeleteMessages(type, id);
  const { isIOS, isDesktop } = useUserPlatform();

  const splideRef = useRef<Splide>(null);
  const imageZoomRef = useRef<ImageZoomActions>(null);

  const handleDelete = async () => {
    if (!message) return;

    if (await promptDeleteMessages([message])) navigate(-1);
  };

  const handleDownload = () => {
    if (!message) return;

    const params = getMessageDownloadParams(message);
    if (!params) return;

    if (message.MsgType === ConversationMessageContentType.File && isIOS) {
      handlePromptDownload(() => handleDownloadUrl(params.url, params.type, params.key)).catch(console.warn);
    } else {
      handleDownloadUrl(params.url, params.type, params.key).catch(console.warn);
    }
  };

  const handlePromptDownload = async (callback: () => Promise<void>) => {
    await makePrompt({
      key: 'download-file',
      title: '',
      content: (
        <div className='flex items-center justify-center gap-1.5'>
          {t('common.download.media-hint')}
          <PicturizedImage
            picture={FileAppPicture}
            className='size-6'
          />
        </div>
      ),
      resolveButtonLabel: t('common.download.confirm'),
      promise: callback,
    });
  };

  const handleForward = () => {
    const href = (type === 'group' ? ROUTES.MESSAGES.GROUPS.FORWARD : ROUTES.MESSAGES.SESSIONS.FORWARD).$buildPath({
      params: {
        id,
      },
      searchParams: {
        messageIds: [messageId],
      },
    });

    navigate(href, { replace: true });
  };

  const handleActiveSlideChange: SplideEventHandlers['onActive'] = (slide) => {
    const activeSlideMessage = messages.at(slide.index);
    if (!activeSlideMessage) return;

    const href = (
      type === 'group' ? ROUTES.MESSAGES.GROUPS.MEDIA.BY_ID : ROUTES.MESSAGES.SESSIONS.MEDIA.BY_ID
    ).$buildPath({
      params: {
        id,
        messageId: activeSlideMessage.ID,
      },
    });
    setMessageId(activeSlideMessage.ID);
    history.replaceState(null, '', href);
  };
  const enableSwipe = (enable: boolean) => {
    const splide = splideRef.current?.splide;
    if (splide) {
      splide.options = {
        ...splide.options,
        drag: enable,
      };
    }
  };

  return (
    <PageLayout
      headerConfig={{
        navigateBack: !isDesktop,
        className: cn('transition-colors', { 'bg-neutral-950 text-neutral-0': shouldDarkMode }),
        headerLabel: message && (
          <div className='flex flex-col text-sm size-full flex-center'>
            <span className='font-bold'>{message.UserNickName}</span>
            <span>{parseCollectionPreviewDate(message.CreateTime)}</span>
          </div>
        ),
        actions: (
          <MediaActions
            conversationType={type}
            imageZoomRef={imageZoomRef}
            mediaType={message?.MsgType}
          />
        ),
      }}
      title={t('conversation.media.title')}
      bannerColor='#000'
      className={cn('flex flex-col gap-1 transition-colors', { 'bg-neutral-950 text-neutral-0': shouldDarkMode })}>
      <DelayedLayout
        delay={300}
        className={cn({ 'flex-1 w-screen': !isDesktop })}>
        {() => (
          <Splide
            ref={splideRef}
            options={{
              perPage: 1,
              perMove: 1,
              flickMaxPages: 1,
              flickPower: 100,
              flickVelocity: 0.5,
              drag: true,
              arrows: false,
              pagination: false,
              start: messageIndex,
              gap: '0.25rem',
              ...(isDesktop ? { width: '60rem' } : { fixedWidth: '100vw' }),
            }}
            className={cn('flex-1', { 'carousel w-screen flex': !isDesktop })}
            onActive={handleActiveSlideChange}>
            {messages.map((m) => (
              <SplideSlide
                key={m.ID}
                className={cn({ 'h-[37.5rem]': isDesktop })}>
                <MediaContent
                  ref={m.ID === messageId && m.MsgType === ConversationMessageContentType.Image ? imageZoomRef : null}
                  autoPlay
                  isInView={m.ID === messageId}
                  type={m.MsgType}
                  src={m.Message}
                  onImageZoom={enableSwipe}
                />
              </SplideSlide>
            ))}
          </Splide>
        )}
      </DelayedLayout>

      <div className={cn('flex items-center justify-around py-6')}>
        <button
          type='button'
          className='btn btn-ghost flex-nowrap flex-col gap-2'
          onClick={handleDelete}>
          <Trash
            variant='Outline'
            size={24}
          />

          <span>{t('conversation.delete.delete')}</span>
        </button>

        <button
          type='button'
          disabled={isDownloading}
          className='btn btn-ghost disabled:invert flex-nowrap flex-col gap-2'
          onClick={handleDownload}>
          {isDownloading ? (
            <span className='loading loading-spinner' />
          ) : (
            <ImportCurve
              variant='Outline'
              size={24}
            />
          )}
          <span>{t('common.save')}</span>
        </button>
        <button
          type='button'
          className='btn btn-ghost flex-nowrap flex-col gap-2'
          onClick={handleForward}>
          <Export
            variant='Outline'
            size={24}
          />
          <span>{t('conversation.forward.forward')}</span>
        </button>
      </div>
    </PageLayout>
  );
}

function MediaActions({
  mediaType,
  imageZoomRef,
  conversationType,
}: {
  mediaType?: ConversationMessageContentType;
  imageZoomRef?: React.RefObject<ImageZoomActions>;
  conversationType: ConversationType;
}) {
  const navigate = useNavigate();
  const historyState = useTypedState(ROUTES.MESSAGES.GROUPS.MEDIA.BY_ID || ROUTES.MESSAGES.SESSIONS.MEDIA.BY_ID);
  const { isDesktop } = useUserPlatform();
  const zoomButtons = [
    { key: 'zoom-in', icon: <SearchZoomIn />, action: () => imageZoomRef?.current?.zoomIn() },
    { key: 'zoom-out', icon: <SearchZoomOut1 />, action: () => imageZoomRef?.current?.zoomOut() },
    { key: 'zoom-fit', icon: <ZoomFit className='size-6' />, action: () => imageZoomRef?.current?.resetTransform() },
  ];
  return isDesktop ? (
    <div className='flex justify-end'>
      <div className='flex items-center gap-4 min-w-fit'>
        {mediaType === ConversationMessageContentType.Image && (
          <>
            {zoomButtons.map(({ key, icon, action }) => (
              <button
                key={key}
                className='btn btn-ghost p-0'
                type='button'
                onClick={action}>
                {icon}
              </button>
            ))}
            <div className='h-6 min-w-[2px] bg-white ' />
          </>
        )}
        {historyState.from === 'message' ? (
          <>
            <CategoryButton conversationType={conversationType} />
            <div className='h-6 min-w-[2px] bg-white' />
          </>
        ) : null}
        <button
          type='button'
          className='btn btn-ghost p-0'
          onClick={() => {
            navigate(-1);
          }}>
          <Add className='rotate-45 size-6' />
        </button>
      </div>
    </div>
  ) : (
    <CategoryButton conversationType={conversationType} />
  );
}

const CategoryButton = ({ conversationType }: { conversationType?: ConversationType }) => {
  const { id } = useTypedParams(ROUTES.MESSAGES.GROUPS.MEDIA.BY_ID || ROUTES.MESSAGES.SESSIONS.MEDIA.BY_ID);

  return (
    <Link
      replace
      className='btn btn-ghost p-0 '
      to={(conversationType === 'group' ? ROUTES.MESSAGES.GROUPS.MEDIA : ROUTES.MESSAGES.SESSIONS.MEDIA).$buildPath({
        params: { id },
      })}>
      <Category variant='Bulk' />
    </Link>
  );
};
